import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { getPasswordResetRedirectUrl, getSignupEmailRedirectUrl } from '../lib/authRedirects'

/**
 * Authentication Context
 * Manages user authentication state across the entire application
 * Provides login, signup, logout, and password reset functionality
 * Now includes user tier management (free/premium) and search limit tracking
 */

// Create the authentication context
const AuthContext = createContext({})
const PROFILE_FETCH_TIMEOUT_MS = 10000

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// AuthProvider component that wraps the app
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [billingCustomer, setBillingCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const profileRequestIdRef = useRef(0)

  const fetchBillingCustomer = useCallback(async (currentUserId) => {
    if (!currentUserId) {
      setBillingCustomer(null)
      return null
    }

    try {
      const { data, error } = await supabase
        .from('billing_customers')
        .select('subscription_status, payment_grace_ends_at, payment_issue_noticed_at, cancel_at_period_end, current_period_end')
        .eq('user_id', currentUserId)
        .maybeSingle()

      if (error) {
        throw error
      }

      setBillingCustomer(data || null)
      return data || null
    } catch (error) {
      console.error('Error fetching billing customer:', error)
      setBillingCustomer(null)
      return null
    }
  }, [])

  const fetchUserProfile = useCallback(async (currentUser) => {
    const requestId = profileRequestIdRef.current + 1
    profileRequestIdRef.current = requestId

    if (!currentUser?.id) {
      setUserProfile(null)
      setBillingCustomer(null)
      setProfileLoading(false)
      return null
    }

    setProfileLoading(true)
    const controller = new AbortController()
    let timeoutId
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort()
        reject(new Error('Profile sync timed out.'))
      }, PROFILE_FETCH_TIMEOUT_MS)
    })

    try {
      console.log('AuthContext: Fetching user profile for:', currentUser.id)

      const profileQuery = supabase.rpc('get_or_create_user_profile', {
        user_id: currentUser.id,
        user_email: currentUser.email || null
      })

      const profilePromise = typeof profileQuery.abortSignal === 'function'
        ? profileQuery.abortSignal(controller.signal)
        : profileQuery

      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise])

      if (profileRequestIdRef.current !== requestId) {
        return null
      }

      if (error) {
        throw error
      }

      console.log('AuthContext: User profile obtained:', profile)
      setUserProfile(profile)

      // Billing status drives the payment-issue grace modal (best-effort; profile remains source of tier)
      await fetchBillingCustomer(currentUser.id)

      if (profileRequestIdRef.current !== requestId) {
        return null
      }

      return profile
    } catch (error) {
      console.error('Error fetching user profile:', error)
      setUserProfile(null)
      setBillingCustomer(null)
      return null
    } finally {
      clearTimeout(timeoutId)
      if (profileRequestIdRef.current === requestId) {
        setProfileLoading(false)
      }
    }
  }, [fetchBillingCustomer])
  const refreshUserProfile = useCallback(async () => {
    if (!user) return null
    return fetchUserProfile(user)
  }, [fetchUserProfile, user])

  // Check for existing session on component mount
  useEffect(() => {
    // Set a timeout to prevent infinite loading (safety net)
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout - forcing loading to false')
      setLoading(false)
    }, 10000) // 10 second timeout
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('AuthContext: Getting initial session...')
        const { data: { session } } = await supabase.auth.getSession()
        console.log('AuthContext: Session retrieved:', !!session?.user)
        setUser(session?.user ?? null)
        console.log('AuthContext: Initial session setup complete')
      } catch (error) {
        console.error('Error in getInitialSession:', error)
      } finally {
        clearTimeout(loadingTimeout)
        console.log('AuthContext: Setting loading to false')
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null)

        if (!session?.user) {
          profileRequestIdRef.current += 1
          setUserProfile(null)
          setBillingCustomer(null)
          setProfileLoading(false)
        }

        setLoading(false)
      }
    )

    return () => {
      subscription?.unsubscribe()
      clearTimeout(loadingTimeout)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      profileRequestIdRef.current += 1
      setUserProfile(null)
      setBillingCustomer(null)
      setProfileLoading(false)
      return undefined
    }

    fetchUserProfile(user)

    return () => {
      profileRequestIdRef.current += 1
    }
  }, [fetchUserProfile, user])

  useEffect(() => {
    if (!user?.id) return undefined

    const channel = supabase
      .channel(`user-profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setUserProfile(null)
            return
          }

          if (payload.new) {
            setUserProfile(payload.new)
          }
        }
      )
      .subscribe((status) => {
        console.log('AuthContext: User profile realtime status:', status)
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user) return undefined

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchUserProfile(user)
      }
    }

    const refreshOnFocus = () => {
      fetchUserProfile(user)
    }

    document.addEventListener('visibilitychange', refreshWhenVisible)
    window.addEventListener('focus', refreshOnFocus)

    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      window.removeEventListener('focus', refreshOnFocus)
    }
  }, [fetchUserProfile, user])

  // Sign up new user
  const signUp = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getSignupEmailRedirectUrl()
      }
    })
    
    return { data, error }
  }, [])

  // Sign in existing user
  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  }, [])

  // Sign out user
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        return { error }
      }

      profileRequestIdRef.current += 1
      setUser(null)
      setUserProfile(null)
      setBillingCustomer(null)
      setProfileLoading(false)

      return { error: null }
    } catch (error) {
      return {
        error: error instanceof Error ? error : new Error('Unable to complete sign-out.')
      }
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (email) => {
    const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''
    if (!normalizedEmail) {
      return { data: null, error: new Error('Please enter a valid email address.') }
    }

    const redirectTo = getPasswordResetRedirectUrl()
    if (!redirectTo) {
      return { data: null, error: new Error('Unable to determine password reset redirect URL.') }
    }

    const { data, error } = await supabase.functions.invoke('forgot-password', {
      body: {
        email: normalizedEmail,
        redirectTo
      }
    })
    return { data, error }
  }, [])

  // Check if user can perform search (now unlimited for all tiers)
  const canSearch = useCallback(() => {
    // All users (free and premium) can search unlimited times
    return !!userProfile
  }, [userProfile])

  // Increment search count for free users
  const incrementSearchCount = useCallback(async () => {
    if (!user || !userProfile) return { error: 'No user logged in' }
    if (userProfile.tier === 'premium') return { success: true } // No limit for premium

    try {
      const lastReset = new Date(userProfile.last_search_reset)
      const now = new Date()
      const hoursSinceReset = (now - lastReset) / (1000 * 60 * 60)
      
      // Reset count if 24 hours have passed
      let newCount = userProfile.search_count + 1
      let newResetTime = userProfile.last_search_reset
      
      if (hoursSinceReset >= 24) {
        newCount = 1
        newResetTime = now.toISOString()
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .update({ 
          search_count: newCount,
          last_search_reset: newResetTime,
          updated_at: now.toISOString()
        })
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      
      // Update local state
      setUserProfile(data)
      return { success: true, data }
    } catch (error) {
      console.error('Error incrementing search count:', error)
      return { error: error.message }
    }
  }, [user, userProfile])

  // Get remaining searches (now unlimited for all users)
  const getRemainingSearches = useCallback(() => {
    // All users have unlimited searches
    return Infinity
  }, [])

  // Context value with all auth functions and state
  const value = useMemo(() => ({
    user,
    userProfile,
    billingCustomer,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    canSearch,
    incrementSearchCount,
    getRemainingSearches,
    refreshUserProfile
  }), [
    billingCustomer,
    canSearch,
    getRemainingSearches,
    incrementSearchCount,
    loading,
    profileLoading,
    refreshUserProfile,
    resetPassword,
    signIn,
    signOut,
    signUp,
    user,
    userProfile
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
